import { useEffect, useRef } from "react";
import * as THREE from "three";

/**
 * AtomPlanet360 — Solo planet 3D viewer (auto-rotate + drag to rotate).
 * Renders the planet as a textured sphere with the item's image_url mapped.
 */
export default function AtomPlanet360({ imageUrl, height = 300, autoRotate = true }) {
  const mountRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!mountRef.current) return;
    const mount = mountRef.current;
    const w = mount.clientWidth || 400;
    const h = height;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x050510);
    const camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 100);
    camera.position.set(0, 0, 4.5);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(w, h);
    mount.appendChild(renderer.domElement);

    scene.add(new THREE.AmbientLight(0xffffff, 0.6));
    const light = new THREE.DirectionalLight(0xffffff, 1.4);
    light.position.set(3, 4, 5);
    scene.add(light);

    const mat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.6, metalness: 0.05 });
    if (imageUrl) {
      const loader = new THREE.TextureLoader();
      loader.setCrossOrigin("anonymous");
      loader.load(
        imageUrl,
        (tex) => { tex.colorSpace = THREE.SRGBColorSpace; mat.map = tex; mat.needsUpdate = true; },
        undefined,
        () => { /* silent */ }
      );
    }
    const sphere = new THREE.Mesh(new THREE.SphereGeometry(1.4, 64, 64), mat);
    scene.add(sphere);

    let drag = null;
    function onDown(ev) {
      const cx = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const cy = ev.touches ? ev.touches[0].clientY : ev.clientY;
      drag = { x: cx, y: cy };
    }
    function onMove(ev) {
      if (!drag) return;
      const cx = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const cy = ev.touches ? ev.touches[0].clientY : ev.clientY;
      sphere.rotation.y += (cx - drag.x) * 0.01;
      sphere.rotation.x += (cy - drag.y) * 0.01;
      drag = { x: cx, y: cy };
    }
    function onUp() { drag = null; }
    renderer.domElement.style.cursor = "grab";
    renderer.domElement.addEventListener("mousedown", onDown);
    renderer.domElement.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    renderer.domElement.addEventListener("touchstart", onDown);
    renderer.domElement.addEventListener("touchmove", onMove);
    window.addEventListener("touchend", onUp);

    function onResize() {
      const nw = mount.clientWidth || 400;
      renderer.setSize(nw, h);
      camera.aspect = nw / h;
      camera.updateProjectionMatrix();
    }
    window.addEventListener("resize", onResize);

    const clock = new THREE.Clock();
    function animate() {
      const dt = clock.getDelta();
      if (autoRotate && !drag) sphere.rotation.y += dt * 0.3;
      renderer.render(scene, camera);
      rafRef.current = requestAnimationFrame(animate);
    }
    animate();

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
      try { mount.removeChild(renderer.domElement); } catch {}
      renderer.dispose();
    };
  }, [imageUrl, height, autoRotate]);

  return <div ref={mountRef} className="rounded-lg overflow-hidden border border-white/10 bg-black" style={{ height }} data-testid="atom-planet-360" />;
}
